<template>
  <div class="payments-card">
    <div class="card-title with-action">
      <div class="card-title-left">
        <SectionIcon name="feePlans" size="40" />
        <h3>{{ voucherLabel }} claims</h3>
      </div>
      <button
        class="action-chip"
        :disabled="!exportable.length"
        @click="exportCsv"
        title="CSV of approved claims for the government redemption portal"
      >
        <i class="bi bi-download"></i>
        Export {{ exportable.length }} for redemption
      </button>
    </div>
    <p class="muted">
      Families claim a {{ pounds(voucherAmount) }} voucher against their season
      fees; approve claims here, then export the approved list for the
      government portal and mark them redeemed when the money arrives.
    </p>

    <div class="filters">
      <button
        v-for="chip in statusChips"
        :key="chip.key"
        class="filter-chip"
        :class="{ active: statusFilter === chip.key }"
        @click="statusFilter = statusFilter === chip.key ? null : chip.key"
      >
        {{ chip.label }} ({{ countByStatus(chip.key) }})
      </button>
    </div>

    <table class="payments-table" v-if="filteredClaims.length > 0">
      <thead>
        <tr>
          <th>Player</th>
          <th>Details</th>
          <th>Year</th>
          <th>Amount</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="claim in filteredClaims" :key="claim.id">
          <td>{{ claim.name }}</td>
          <td class="details-cell">{{ detailsSummary(claim) }}</td>
          <td>{{ claim.calendar_year }}</td>
          <td>{{ pounds(claim.amount_pence) }}</td>
          <td>
            <span class="status-pill" :class="'voucher-' + claim.status">
              {{ claim.status }}
            </span>
          </td>
          <td class="row-actions">
            <template v-if="claim.status === 'claimed'">
              <button class="link-button" :disabled="busy" @click="decide(claim, 'approved')">
                Approve
              </button>
              <button class="link-button danger" :disabled="busy" @click="decide(claim, 'rejected')">
                Reject
              </button>
            </template>
            <button
              v-else-if="claim.status === 'approved'"
              class="link-button"
              :disabled="busy"
              @click="markRedeemed(claim)"
            >
              Mark redeemed
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else class="muted">No voucher claims{{ statusFilter ? " with this status" : " yet" }}.</p>
    <p class="error-text" v-if="error">{{ error }}</p>
  </div>
</template>

<script>
import { StripeConnect } from "@/services/stripe-connect-service.js";
import {
  getPaymentsCurrency,
  getVoucherConfig,
} from "@/config/tenant";
import SectionIcon from "@/components/SectionIcon/index.vue";

export default {
  name: "ClubVouchers",
  components: { SectionIcon },
  props: {
    clubId: { type: String, required: true },
  },
  data() {
    return {
      claims: [],
      statusFilter: "claimed",
      busy: false,
      error: "",
      statusChips: [
        { key: "claimed", label: "Awaiting approval" },
        { key: "approved", label: "Approved" },
        { key: "redeemed", label: "Redeemed" },
        { key: "rejected", label: "Rejected" },
      ],
    };
  },
  computed: {
    voucherLabel() {
      return getVoucherConfig()?.label ?? "Sports Voucher";
    },
    voucherAmount() {
      return getVoucherConfig()?.amount_pence ?? 0;
    },
    filteredClaims() {
      if (!this.statusFilter) return this.claims;
      return this.claims.filter((c) => c.status === this.statusFilter);
    },
    exportable() {
      return this.claims.filter((c) => c.status === "approved");
    },
  },
  created() {
    this.load();
  },
  methods: {
    pounds(amountPence) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: getPaymentsCurrency(),
      }).format((amountPence || 0) / 100);
    },
    countByStatus(status) {
      return this.claims.filter((c) => c.status === status).length;
    },
    parsedDetails(claim) {
      try {
        return JSON.parse(claim.details) || {};
      } catch (e) {
        return {};
      }
    },
    detailsSummary(claim) {
      const d = this.parsedDetails(claim);
      return [d.child_name, d.school, d.year_level].filter(Boolean).join(" · ") || "—";
    },
    async load() {
      try {
        const response = await StripeConnect.listVoucherClaims(this.clubId);
        this.claims = response.data.data || [];
      } catch (error) {
        this.error =
          error?.response?.data?.error || "Could not load voucher claims.";
      }
    },
    async decide(claim, decision) {
      this.busy = true;
      this.error = "";
      try {
        await StripeConnect.decideVoucher(this.clubId, claim.id, decision);
        await this.load();
      } catch (error) {
        this.error = error?.response?.data?.error || "Could not update the claim.";
      } finally {
        this.busy = false;
      }
    },
    async markRedeemed(claim) {
      this.busy = true;
      this.error = "";
      try {
        await StripeConnect.markVouchersRedeemed(this.clubId, [claim.id]);
        await this.load();
      } catch (error) {
        this.error = error?.response?.data?.error || "Could not mark redeemed.";
      } finally {
        this.busy = false;
      }
    },
    exportCsv() {
      const rows = [
        ["Player", "Child name", "DOB", "School", "Year level", "Amount", "Claimed", "Approved"],
        ...this.exportable.map((c) => {
          const d = this.parsedDetails(c);
          return [
            c.name,
            d.child_name || c.name,
            c.dob || "",
            d.school || "",
            d.year_level || "",
            ((c.amount_pence || 0) / 100).toFixed(2),
            c.created_at,
            c.decided_at || "",
          ];
        }),
      ];
      const csv = rows
        .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `voucher-redemptions-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style scoped lang="scss">
.payments-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .card-title-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  h3 {
    margin: 0;
    font-size: 1.05rem;
  }
}

.muted {
  color: #6c757d;
  font-size: 0.85rem;
}

.filters {
  display: flex;
  gap: 8px;
  margin: 10px 0;
  flex-wrap: wrap;
}

.filter-chip {
  border: 1px solid #ced4da;
  background: #fff;
  color: #343a40;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 0.8rem;
  cursor: pointer;

  &.active {
    background: #343a40;
    color: #fff;
    border-color: #343a40;
  }
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #f1f3f5;
  }
}

.details-cell {
  color: #495057;
  font-size: 0.82rem;
}

.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;

  &.voucher-claimed {
    background: #fff3cd;
    color: #856404;
  }

  &.voucher-approved {
    background: #d1e7dd;
    color: #0f5132;
  }

  &.voucher-redeemed {
    background: #cfe2ff;
    color: #084298;
  }

  &.voucher-rejected {
    background: #f8d7da;
    color: #842029;
  }
}

.row-actions {
  white-space: nowrap;

  .link-button + .link-button {
    margin-left: 10px;
  }
}

.link-button {
  border: none;
  background: none;
  color: #0d6efd;
  cursor: pointer;
  font-size: 0.83rem;
  padding: 0;
  text-decoration: underline;

  &.danger {
    color: #dc3545;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.action-chip {
  border: 1px solid #ced4da;
  background: #fff;
  color: #212529;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.83rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.error-text {
  color: #dc3545;
  font-size: 0.85rem;
}
</style>
